import React from "react";
import {
  FiX,
  FiUser,
  FiPhone,
  FiMail,
  FiCalendar,
  FiFileText,
  FiFacebook,
  FiInstagram,
  FiCheckCircle,
  FiXCircle,
  FiTag,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const ExpertApplicationDetails = ({
  application,
  open,
  onClose,
  statusLabels,
  statusColors,
  onConfirm,
  onReject,
}) => {
  const { t, i18n } = useTranslation();

  if (!open || !application) return null;

  const dateLocale = i18n.language === "en" ? "en-US" : "ar-EG";

  const cvUrl = application.cv
    ? process.env.REACT_APP_IMAGE_URL + application.cv
    : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-purple-700 rounded-t-3xl p-6 text-white">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2 text-left rtl:text-right">
                  {t("dashboard.expertApplications.details.titlePrefix")} #
                  {application.id}
                </h2>
                <div className="flex justify-end">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      statusColors?.[application.status] ||
                      "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {statusLabels?.[application.status] || application.status}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-2 hover:bg-white hover:bg-opacity-20 rounded-xl transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto p-6 space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-left rtl:text-right">
                {t("dashboard.expertApplications.details.sections.applicantInfo")}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("dashboard.expertApplications.common.fields.name")}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {application.name}
                    </span>
                    <FiUser className="w-4 h-4 text-blue-500" />
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("dashboard.expertApplications.common.fields.phone")}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {application.number}
                    </span>
                    <FiPhone className="w-4 h-4 text-green-500" />
                  </div>
                </div>
                {application.email && (
                  <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t("dashboard.expertApplications.common.fields.email")}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {application.email}
                      </span>
                      <FiMail className="w-4 h-4 text-purple-500" />
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("dashboard.expertApplications.common.fields.submittedAt")}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {new Date(application.createdAt).toLocaleString(
                        dateLocale
                      )}
                    </span>
                    <FiCalendar className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("dashboard.expertApplications.details.accountType.label")}
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {application.user_id
                      ? t(
                          "dashboard.expertApplications.details.accountType.existingUser"
                        )
                      : t(
                          "dashboard.expertApplications.details.accountType.newUser"
                        )}
                  </span>
                </div>
              </div>
            </div>

            {application.experience && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 text-left rtl:text-right">
                  {t("dashboard.expertApplications.details.sections.experience")}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 text-left rtl:text-right whitespace-pre-wrap">
                  {application.experience}
                </p>
              </div>
            )}

            {application.categories?.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 text-left rtl:text-right flex items-center justify-end gap-2">
                  <FiTag className="w-5 h-5" />
                  {t("dashboard.expertApplications.common.fields.categories")}
                </h3>
                <div className="flex flex-wrap gap-2 justify-end">
                  {application.categories.map((c) => (
                    <span
                      key={c.id}
                      className="px-3 py-1 text-sm rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                    >
                      {c.category?.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(application.facebook ||
              application.instagram ||
              application.tiktok ||
              cvUrl) && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 text-left rtl:text-right">
                  {t("dashboard.expertApplications.details.sections.additionalLinks")}
                </h3>
                <div className="flex flex-wrap gap-3 justify-end">
                  {cvUrl && (
                    <a
                      href={cvUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    >
                      <FiFileText className="w-4 h-4" />
                      {t("dashboard.expertApplications.details.links.viewCv")}
                    </a>
                  )}
                  {application.facebook && (
                    <a
                      href={application.facebook}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                    >
                      <FiFacebook className="w-4 h-4" />
                      {t("dashboard.expertApplications.details.links.facebook")}
                    </a>
                  )}
                  {application.instagram && (
                    <a
                      href={application.instagram}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                    >
                      <FiInstagram className="w-4 h-4" />
                      {t("dashboard.expertApplications.details.links.instagram")}
                    </a>
                  )}
                  {application.tiktok && (
                    <a
                      href={application.tiktok}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                    >
                      {t("dashboard.expertApplications.details.links.tiktok")}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-b-3xl">
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t("dashboard.expertApplications.details.actions.close")}
              </button>
              {application.status === "Pending" && (
                <>
                  <button
                    onClick={() => onReject(application.id, application.name)}
                    className="px-6 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center gap-2"
                  >
                    <FiXCircle className="w-4 h-4" />
                    {t("dashboard.expertApplications.details.actions.reject")}
                  </button>
                  <button
                    onClick={() =>
                      onConfirm(application.id, application.name)
                    }
                    className="px-6 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <FiCheckCircle className="w-4 h-4" />
                    {t("dashboard.expertApplications.details.actions.approve")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertApplicationDetails;
